import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, IconEnum } from '@investoro/core';
import PropertyValuationContext from 'utils/context/PropertyValuationContext';
import { stringToNumber } from 'utils/formatters/stringToNumber';
import { generateOptionsFromEnum } from 'utils/generateOptionsFromEnum';
import { roomOptions, userFloorOptions, userFloorsOptions } from 'utils/options';
import { BuildingConditionEnum, BuildingOverallConditionEnum } from 'utils/types/AiPredictorModels';
import { InputType } from 'utils/types/InputTypes';
import { FlatBuildingTypeEnum, TypeOfMarketEnum } from 'utils/types/RentModels';

import StickyBottom from 'components/common/FiltersRenderer/StickyBottom';
import V2FiltersRenderer from 'components/common/FiltersRenderer/V2FiltersRenderer';
import InputContainer from 'components/common/InputContainer';
import { activeNotification } from 'components/functions/activeNotification';
import { v2getVoivodeshipCities } from 'components/functions/locations';

const FiltersValuation = () => {
    const { valuationState, valuationFiltersState, handleChange, clearFilters, fetchPriceRange, loadSavedReport, deleteSavedReport } = useContext(PropertyValuationContext);

    const currentYear = new Date().getFullYear();
    const resultRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!valuationState.isLoading && valuationState.valuationResultV2) {
            resultRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [valuationState.isLoading, valuationState.valuationResultV2]);

    const [isButtonVisible, setIsButtonVisible] = useState(false);

    const getCities = async (voivodeshipId: number) => {
        const newLocations = await v2getVoivodeshipCities(
            voivodeshipId,
            valuationFiltersState.locations
        );
        handleChange('locations', newLocations || []);
    };

    const handleRemoveSavedLocation = (locationId: number) => {
        const savedLocations = valuationFiltersState.savedLocations;

        delete savedLocations[locationId];

        handleChange('savedLocations', savedLocations);
        handleChange('cityName', '');
    };

    const isAreaValid = () => {
        const value = stringToNumber(valuationFiltersState.area);

        return value !== undefined && value >= 10 && value <= 999;
    };

    const isBuildYearValid = () => {
        const value = Number(valuationFiltersState.builtYear);

        return !isNaN(value) && value >= 1900 && value <= currentYear;
    };

    const isRequiredFieldsEmpty = [
        Object.keys(valuationFiltersState.savedLocations).length === 0,
        valuationFiltersState.builtYear === '',
        valuationFiltersState.area === '',
        valuationFiltersState.numberOfRooms === 0,
        !valuationFiltersState.buildingCondition,
        !valuationFiltersState.buildingType,
        !valuationFiltersState.typeOfMarket,
        valuationFiltersState.floor === 0,
        valuationFiltersState.floors === 0
    ].some(Boolean);

    const handleSubmit = async () => {
        if (isRequiredFieldsEmpty) {
            activeNotification('Popraw wpisane dane', 'Musisz wypełnić wszystkie pola', 'warning');

            return;
        }

        if (!isAreaValid() || !isBuildYearValid()) {
            activeNotification('Popraw wpisane dane', 'Wpisz poprawne wartości metrażu lub/i roku budowy', 'warning');

            return;
        }

        await fetchPriceRange();
    };

    const showPriceRange = Boolean(valuationState.priceRangeResponse);

    const savedReports = valuationState.savedReports;

    const savedReportOptions = savedReports.map((report, index) => ({
        id: index,
        name: report.title || report.location || 'Raport bez tytułu'
    }));

    const selectedReportIndex = savedReports.findIndex(
        (report) => report.uuid === valuationState.selectedReportUuid
    );

    const handleSelectReport = (id: number | string) => {
        const index = Number(id);
        const report = savedReports[index];

        if (report?.uuid) {
            loadSavedReport(report.uuid);
        }
    };

    const handleDeleteReport = (id: number | string) => {
        const index = Number(id);
        const report = savedReports[index];

        if (report?.uuid) {
            deleteSavedReport(report.uuid);
        }
    };

    const valuationPropertyStructure = [
        ...savedReportOptions.length > 0
            ? [
                {
                    accordionTitle: 'Zapisane wyceny',
                    hideTitle: true,
                    filters: [
                        {
                            title: 'Zapisane wyceny',
                            type: InputType.FILTER_AUTOCOMPLETE,
                            icon: IconEnum.BOOKMARK,
                            placeholder: 'Wybierz raport...',
                            options: {
                                [InputType.FILTER_AUTOCOMPLETE]: {
                                    contextFunctions: {
                                        handleDeleteFilter: handleDeleteReport,
                                        handleSetFiltersValues: handleSelectReport
                                    },
                                    modulesProps: {
                                        filterId: selectedReportIndex >= 0 ? selectedReportIndex : null,
                                        options: savedReportOptions
                                    }
                                }
                            }
                        }
                    ]
                }
            ]
            : [],
        {
            accordionTitle: 'Podstawowe',
            hideTitle: true,
            filters: [
                {
                    title: 'Lokalizacja',
                    type: InputType.LOCATION,
                    helperText: 'Wybierz z listy interesującą Ciebie lokalizację. Możesz zaznaczyć dowolną ilość.',
                    icon: IconEnum.MAP_PIN,
                    placeholder: 'Szukaj lokalizacji...',
                    options: {
                        [InputType.LOCATION]: {
                            modulesProps: {
                                locations: valuationFiltersState.locations,
                                searchLocationResult: valuationFiltersState.searchLocationResult,
                                savedLocations: valuationFiltersState.savedLocations,
                                singleValue: true,
                                required: true,
                                savedLocationsFieldName: 'savedLocations',
                                cityFieldName: 'city',
                                hideTopLevelCheckboxes: true
                            },
                            contextFunctions: {
                                getCities,
                                handleRemoveSavedLocation,
                                handleChange,
                                handleChangeSingleValueField: handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Numer budynku i mieszkania',
                    helperText: 'Wpisz numer budynku/mieszkania',
                    type: InputType.TEXT,
                    icon: IconEnum.CALENDAR,
                    placeholder: 'Wpisz numer budynku/mieszkania...',
                    options: {
                        [InputType.TEXT]: {
                            modulesProps: {
                                value: valuationFiltersState.buildingNumber,
                                fieldName: 'buildingNumber',
                                maxLength: 200
                            },
                            contextFunctions: {
                                handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Metraż',
                    helperText: 'Metraż musi wynosić od 10 do 999 m²',
                    type: InputType.NUMERIC,
                    icon: IconEnum.CALENDAR,
                    placeholder: 'Wpisz metraż...',
                    options: {
                        [InputType.NUMERIC]: {
                            modulesProps: {
                                value: valuationFiltersState.area,
                                fieldName: 'area',
                                suffix: 'm²',
                                required: true,
                                maxLength: 6,
                                max: 999,
                                decimalScale: 2,
                                decimalSeparator: ','
                            },
                            contextFunctions: {
                                handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Pokoje',
                    type: InputType.AUTOCOMPLETE,
                    icon: IconEnum.BED,
                    placeholder: 'Wybierz liczbę pokoi...',
                    options: {
                        [InputType.AUTOCOMPLETE]: {
                            modulesProps: {
                                value: valuationFiltersState.numberOfRooms === 0 ? '' : valuationFiltersState.numberOfRooms.toString(),
                                fieldName: 'numberOfRooms',
                                options: roomOptions,
                                required: true
                            },
                            contextFunctions: {
                                handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Piętro',
                    type: InputType.AUTOCOMPLETE,
                    icon: IconEnum.BED,
                    placeholder: 'Wybierz piętro...',
                    options: {
                        [InputType.AUTOCOMPLETE]: {
                            modulesProps: {
                                value: valuationFiltersState.floor === 0 ? '' : valuationFiltersState.floor.toString(),
                                fieldName: 'floor',
                                options: userFloorOptions,
                                required: true
                            },
                            contextFunctions: {
                                handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Liczba pięter',
                    type: InputType.AUTOCOMPLETE,
                    icon: IconEnum.BED,
                    placeholder: 'Wybierz liczbę pięter...',
                    options: {
                        [InputType.AUTOCOMPLETE]: {
                            modulesProps: {
                                value: valuationFiltersState.floors === 0 ? '' : valuationFiltersState.floors.toString(),
                                fieldName: 'floors',
                                options: userFloorsOptions,
                                required: true
                            },
                            contextFunctions: {
                                handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Rok budowy',
                    type: InputType.NUMERIC,
                    icon: IconEnum.SQUARE,
                    helperText: `Rok budowy musi być od 1900 do ${currentYear}`,
                    placeholder: 'Wpisz rok budowy...',
                    options: {
                        [InputType.NUMERIC]: {
                            modulesProps: {
                                value: valuationFiltersState.builtYear,
                                fieldName: 'builtYear',
                                suffix: 'rok',
                                required: true,
                                maxLength: 4
                            },
                            contextFunctions: {
                                handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Stan mieszkania',
                    type: InputType.AUTOCOMPLETE,
                    icon: IconEnum.LEAVE,
                    placeholder: 'Wybierz typ...',
                    options: {
                        [InputType.AUTOCOMPLETE]: {
                            contextFunctions: {
                                handleChange
                            },
                            modulesProps: {
                                fieldName: 'buildingCondition',
                                value: valuationFiltersState.buildingCondition || '',
                                options: generateOptionsFromEnum(BuildingConditionEnum),
                                required: true
                            }
                        }
                    }
                },
                {
                    title: 'Stan budynku',
                    type: InputType.AUTOCOMPLETE,
                    icon: IconEnum.BUILDING,
                    placeholder: 'Wybierz typ...',
                    options: {
                        [InputType.AUTOCOMPLETE]: {
                            contextFunctions: {
                                handleChange
                            },
                            modulesProps: {
                                fieldName: 'buildingOverallCondition',
                                value: valuationFiltersState.buildingOverallCondition || '',
                                options: generateOptionsFromEnum(BuildingOverallConditionEnum),
                                required: true
                            }
                        }
                    }
                },
                {
                    title: 'Typ budynku',
                    type: InputType.AUTOCOMPLETE,
                    icon: IconEnum.BED,
                    placeholder: 'Wybierz liczbę pięter...',
                    options: {
                        [InputType.AUTOCOMPLETE]: {
                            modulesProps: {
                                value: valuationFiltersState.buildingType || '',
                                fieldName: 'buildingType',
                                options: generateOptionsFromEnum(FlatBuildingTypeEnum),
                                required: true
                            },
                            contextFunctions: {
                                handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Rodzaj rynku',
                    type: InputType.AUTOCOMPLETE,
                    icon: IconEnum.WORK,
                    placeholder: 'Wybierz rodzaj rynku...',
                    options: {
                        [InputType.AUTOCOMPLETE]: {
                            modulesProps: {
                                value: valuationFiltersState.typeOfMarket || '',
                                fieldName: 'typeOfMarket',
                                options: generateOptionsFromEnum(TypeOfMarketEnum),
                                required: true
                            },
                            contextFunctions: {
                                handleChange
                            }
                        }
                    }
                }
                // {
                //     title: 'Dodatkowe informacje',
                //     type: InputType.MULTIPLE_SELECT,
                //     icon: IconEnum.INFO,
                //     helperText: 'Wybierz dodatkowe udogodnienia nieruchomości.',
                //     placeholder: 'Wybierz udogodnienia...',
                //     options: {
                //         [InputType.MULTIPLE_SELECT]: {
                //             modulesProps: {
                //                 value: valuationFiltersState.additionalInfo || [],
                //                 options: generateOptionsFromEnum(AdditionalInformationEnum),
                //                 optionsIcon: IconEnum.INFO,
                //                 fieldName: 'additionalInfo'
                //             },
                //             contextFunctions: {
                //                 handleChange
                //             }
                //         }
                //     }
                // }
            ]
        },
        // {
        //     accordionTitle: 'Opis',
        //     hideTitle: true,
        //     oneItem: true,
        //     fullWidth: true,
        //     filters: [
        //         {
        //             title: 'Opis',
        //             type: InputType.TEXT,
        //             icon: IconEnum.EDIT,
        //             placeholder: 'Opis...',
        //             helperText: 'Opis może zawierać 1000 znaków maksymalnie.',
        //             options: {
        //                 [InputType.TEXT]: {
        //                     modulesProps: {
        //                         value: valuationFiltersState.description || '',
        //                         fieldName: 'description',
        //                         textareaMode: width < 1024,
        //                         maxLength: 1000,
        //                         halfHeight: true
        //                     },
        //                     contextFunctions: {
        //                         handleChange
        //                     }
        //                 }
        //             }
        //         }
        //     ]
        // },
        ...showPriceRange
            ? []
            : [
                {
                    accordionTitle: 'Podsumowanie',
                    hideTitle: true,
                    isWideSpace: true,
                    filters: [
                        {
                            title: 'Dalej',
                            type: InputType.SAVE_BUTTON,
                            icon: IconEnum.DICTIONARY,
                            placeholder: 'Dalej...',
                            options: {
                                [InputType.SAVE_BUTTON]: {
                                    contextFunctions: {
                                        onActionClick: clearFilters,
                                        onClick: handleSubmit,
                                        onChangeVisibleButtonFilter: setIsButtonVisible
                                    },
                                    modulesProps: {
                                        actionText: 'Resetuj',
                                        buttonText: 'Dalej'
                                    }
                                }
                            }
                        }
                    ]
                }
            ]
    ];

    return (
        <V2FiltersRenderer
            elementsToRender={valuationPropertyStructure}
            type={'property_valuation'}
            bottomActions={
                <StickyBottom
                    isVisible={isButtonVisible && !showPriceRange}
                    className={'sticky-filter-button'}>
                    <InputContainer title={'Dalej'} icon={IconEnum.DICTIONARY}
                        actionText={'Resetuj'}
                        onActionClick={clearFilters}>
                        <Button onClick={handleSubmit}>
                            {'Dalej'}
                        </Button>
                    </InputContainer>
                </StickyBottom>
            }
        />
    );
};

export default FiltersValuation;
